import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const COLOR_THEMES = {
  blue: {
    border: 'border-blue-500/30',
    headerBg: 'from-blue-900/30 to-gray-900',
    accent: 'text-blue-400',
    barBg: 'bg-blue-500/20',
    barFill: 'bg-blue-400',
    dot: 'bg-blue-400',
    pillBg: 'bg-blue-500/10',
    pillText: 'text-blue-300',
    channelBorder: 'border-blue-500/20',
  },
  green: {
    border: 'border-[#749555]/30',
    headerBg: 'from-[#749555]/20 to-gray-900',
    accent: 'text-[#8AB563]',
    barBg: 'bg-[#749555]/20',
    barFill: 'bg-[#8AB563]',
    dot: 'bg-[#8AB563]',
    pillBg: 'bg-[#749555]/10',
    pillText: 'text-[#8AB563]',
    channelBorder: 'border-[#749555]/20',
  },
  gold: {
    border: 'border-[#FFD700]/30',
    headerBg: 'from-[#FFD700]/15 to-gray-900',
    accent: 'text-[#FFD700]',
    barBg: 'bg-[#FFD700]/20',
    barFill: 'bg-[#FFD700]',
    dot: 'bg-[#FFD700]',
    pillBg: 'bg-[#FFD700]/10',
    pillText: 'text-[#FFD700]',
    channelBorder: 'border-[#FFD700]/20',
  },
};

const MiniProgressBar = ({ label, value, target, theme }) => {
  const pct = target ? Math.min((value / target) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-[#9CA3AF] whitespace-nowrap">{label}</span>
      {target ? (
        <>
          <div className={`w-16 h-1.5 ${theme.barBg} rounded-full overflow-hidden`}>
            <div className={`h-full ${theme.barFill} rounded-full transition-all`} style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[#D1D5DB] font-medium">{value}/{target}</span>
        </>
      ) : (
        <span className="text-[#D1D5DB] font-medium">{value}</span>
      )}
    </div>
  );
};

const EngineSection = ({ name, color = 'blue', defaultExpanded = false, progressBars = [], rows = [], channels = [] }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const navigate = useNavigate();
  const theme = COLOR_THEMES[color] || COLOR_THEMES.blue;

  return (
    <div className={`bg-gray-800 border ${theme.border} rounded-xl overflow-hidden transition-all flex flex-col`}>
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r ${theme.headerBg} transition-colors hover:brightness-110`}
      >
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${theme.dot}`} />
          <span className={`font-semibold text-sm ${theme.accent}`}>{name}</span>
          <span className={`text-[10px] transition-transform ${expanded ? 'rotate-180' : ''}`}>
            &#9660;
          </span>
        </div>
        <div className="flex items-center gap-4">
          {progressBars.map((bar, i) => (
            <MiniProgressBar key={i} label={bar.label} value={bar.value} target={bar.target} theme={theme} />
          ))}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 py-3 border-t border-gray-700/50 flex flex-col flex-1">
          {/* Status rows */}
          <div className="space-y-1">
          {rows.map((row, i) => {
            const prevGroup = i > 0 ? rows[i - 1].group : null;
            const showGroupHeader = row.group && row.group !== prevGroup;
            return (
              <React.Fragment key={i}>
                {showGroupHeader && (
                  <div className={`flex items-center gap-2 ${i > 0 ? 'mt-2 pt-2 border-t border-gray-700/30' : ''}`}>
                    {row.groupIcon && (
                      <FontAwesomeIcon icon={row.groupIcon} className={`text-[11px] ${row.groupIconColor || 'text-[#6B7280]'}`} />
                    )}
                    <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">{row.group}</span>
                  </div>
                )}
                <div className={`flex items-center justify-between py-1.5 ${row.group ? 'pl-5 pr-2' : 'px-2'} rounded-lg hover:bg-gray-700/30 transition-colors`}>
                  <div className="flex items-center gap-3">
                    {row.faIcon ? (
                      <FontAwesomeIcon icon={row.faIcon} className={`text-base w-5 ${row.iconColor || 'text-[#9CA3AF]'}`} />
                    ) : (
                      <span className="text-lg w-5 text-center">{row.icon}</span>
                    )}
                    <div className="flex flex-col">
                      <span className="text-sm text-[#F9FAFB] font-medium leading-tight">{row.label}</span>
                      <span className="text-[11px] text-[#9CA3AF] leading-tight">{row.value}</span>
                    </div>
                  </div>
                  {row.action ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (row.onClick) row.onClick();
                        else if (row.route) navigate(row.route);
                        else if (row.href) window.open(row.href, '_blank');
                      }}
                      className={`text-xs font-medium px-3 py-1.5 rounded-lg ${theme.pillBg} ${theme.accent} hover:brightness-125 transition-all`}
                    >
                      {row.action}
                    </button>
                  ) : row.schedule ? (
                    <span
                      className={`text-[11px] text-gray-500 ${row.route || row.href ? 'cursor-pointer hover:text-gray-400 transition-colors' : 'cursor-default'}`}
                      title={row.scheduleTooltip}
                      onClick={(e) => {
                        if (row.route) { e.stopPropagation(); navigate(row.route); }
                        else if (row.href) { e.stopPropagation(); window.open(row.href, '_blank'); }
                      }}
                    >
                      {row.schedule}
                    </span>
                  ) : null}
                </div>
              </React.Fragment>
            );
          })}
          </div>

          {/* Channels — pinned to bottom */}
          {channels.length > 0 && (
            <div className={`mt-auto border-t ${theme.channelBorder} pt-3`}>
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Channels</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {channels.map((ch, i) => (
                  <a
                    key={i}
                    href={ch.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-[#1F2937] border border-[#374151] hover:border-[#4B5563] text-[#D1D5DB] hover:text-white transition-all group"
                    title={ch.description}
                  >
                    <FontAwesomeIcon icon={ch.icon} className={`text-[11px] ${ch.iconColor || 'text-[#9CA3AF]'} group-hover:brightness-125`} />
                    <span>{ch.handle}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EngineSection;
